import { memo , useEffect} from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  Layout,
  PageTitle,
  RoundedCheckIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { CopyStrokeIcon } from "../assets/icons";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRequestStore } from "../store/useRequestStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useCopyLink } from "../hooks/useCopyLink";
import { getEvents, safeCapture } from "../analytics";
import { buildShareText, shareOrCopyMessage } from "../utils/shareMessage";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";

/**
 * Terminal screen for the request flow. The link box copies the URL only.
 * Share sends a localized message that names the requester when we have one.
 */
export const RequestReadyPage = memo(() => {
  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("done"));
  }, []);

  const { t, i18n } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { mintResult, draft } = useRequestStore();
  const { redirectTo } = useRouteWithToken();

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    dropzoneColors,
    backgroundIcon,
  } = colorConfig;

  const shareLink = mintResult?.url ?? "";

  const { copied, copyLink } = useCopyLink();

  const handleCopy = () => {
    if (!shareLink) return;
    safeCapture(getEvents().SHARE_CLICK, { action: "copy" });
    void copyLink(shareLink, t("share.manualPrompt"));
  };

  const handleShare = async () => {
    if (!shareLink) return;
    safeCapture(getEvents().SHARE_CLICK, { action: "share" });
    const message = buildShareText(t, {
      firstName: session.data?.userData?.name?.firstName,
      amount: draft?.amount,
      currency: draft?.destinationCurrency,
      url: shareLink,
      language: i18n.language,
    });
    await shareOrCopyMessage(message, {
      title: t("share.shareTitle"),
      fallbackPrompt: t("share.manualPrompt"),
    });
  };

  return (
    <AuraLayout colorConfig={colorConfig} className="share-ready-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <RoundedCheckIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
          <PageTitle
            highlight={t("share.title")}
            highlightColor={TITLE_COLOR}
            secudnary={t("share.subtitle")}
            secudnaryColor={TITLE_COLOR}
          />
          <button
            type="button"
            className={`request-link-box ${copied ? "request-link-box--copied" : ""}`.trim()}
            onClick={handleCopy}
            disabled={!shareLink}
            aria-label={copied ? t("share.copied") : t("share.copyLink")}
          >
            <BodyCopy
              textColor={bodyCopy}
              className="request-link-value"
              aria-live="polite"
            >
              {copied ? t("share.copied") : shareLink || "—"}
            </BodyCopy>
            <span className="request-link-copy" aria-hidden="true">
              <CopyStrokeIcon size={20} colors={[bodyCopy, bodyCopy]} />
            </span>
          </button>
        </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section share-ready-actions">
          <Button
            type="button"
            size="large"
            variant="outline"
            background={backgroundBtn}
            color={TITLE_COLOR}
            style={{ backgroundColor: background }}
            onClick={() => redirectTo(APP_ROUTES.HOME_PAGE)}
          >
            {t("share.home")}
          </Button>
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => void handleShare()}
          >
            {t("share.share")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
